import React, { useState } from "react";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  Mountain,
  TrendingUp,
  ExternalLink,
  MapPinned,
  Calendar,
  Users,
  Camera,
} from "lucide-react";
import { fetchPeak, type PeakResponse } from "../api/features";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { JsonLd } from "../components/JsonLd";
import AppShell from "../layouts/AppShell";
import { AtlasPanel } from "../components/AtlasPanel";
import { StatBlock } from "../components/StatBlock";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { CollectionCard } from "../components/CollectionCard";
import { CollectibleItem } from "../components/CollectibleItem";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { GuestTopBar } from "../components/GuestTopBar";
import { AddToListButton } from "../components/AddToListButton";
import { StarRating } from "../components/StarRating";
import { placeOf } from "../lib/place-names";
import type { CreateCheckinResponse } from "../api/landmarks";
import { CheckinDialog, CheckinGallery, checkinGalleryKey } from "../components/PhotoCheckin";

function PeakContent({ data, isAuthenticated }: { data: PeakResponse; isAuthenticated: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { peak, trigPillar, visits, collections, nearbyUncollected, rating } = data;
  const summited = visits.length > 0;
  const checkinTarget = {
    kind: "peak" as const,
    slug: peak.slug,
    name: peak.name,
    latitude: peak.latitude,
    longitude: peak.longitude,
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<CreateCheckinResponse | null>(null);

  function handleCheckinSuccess(result: CreateCheckinResponse) {
    setDialogOpen(false);
    setLastResult(result);
    // A summit check-in collects the peak: refetch everything that could move
    // (visits, collections, map pins, badges); the level-up fanfare rides the
    // usual profile refetch.
    void queryClient.invalidateQueries({ queryKey: ["peaks", peak.slug] });
    void queryClient.invalidateQueries({ queryKey: checkinGalleryKey(checkinTarget) });
    void queryClient.invalidateQueries({ queryKey: ["collections"] });
    void queryClient.invalidateQueries({ queryKey: ["map"] });
    void queryClient.invalidateQueries({ queryKey: ["map/features"] });
    void queryClient.invalidateQueries({ queryKey: ["achievements"] });
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  const canonicalPath = `/peaks/${peak.slug}`;
  // Matches placeOf() in scripts/prerender.mjs (build-time static pages) so
  // the two SEO layers agree once client JS hydrates.
  const place = placeOf(peak.region, peak.country);
  usePageMeta({
    title: `${peak.name} - ${peak.elevationM} m peak in ${place} | Atlas`,
    description: `${peak.name} is a ${peak.elevationM} m peak in ${place}${peak.prominenceM != null ? `, with ${peak.prominenceM} m of prominence` : ""}. Track your summit and see nearby uncollected peaks on Atlas.`,
    canonicalPath,
  });

  return (
    <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Mountain",
          name: peak.name,
          url: `https://goatlas.co.uk${canonicalPath}`,
          geo: {
            "@type": "GeoCoordinates",
            latitude: peak.latitude,
            longitude: peak.longitude,
          },
          elevation: `${peak.elevationM} m`,
        }}
      />
      <button
        type="button"
        onClick={() => void navigate(isAuthenticated ? { to: "/map" } : { to: "/" })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          padding: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <ChevronLeft size={18} /> {isAuthenticated ? "Map" : "Atlas"}
      </button>

      {/* hero */}
      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
          flexShrink: 0,
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: 28, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {[peak.region, peak.country].filter(Boolean).join(" · ") || "Peak"}
            </div>
            <h1
              style={{
                font: "var(--type-display)",
                fontSize: 44,
                color: "var(--text-primary)",
                margin: "0 0 12px",
                letterSpacing: "-.02em",
              }}
            >
              {peak.name}
            </h1>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {summited ? (
                <Badge variant="gold" dot>
                  Summited{visits.length > 1 ? ` · ${visits.length} times` : ""}
                </Badge>
              ) : (
                <Badge variant="neutral">Not yet summited</Badge>
              )}
              {collections.map((col) => (
                <Badge key={col.slug} variant="neutral">
                  {col.name}
                </Badge>
              ))}
              {trigPillar && (
                <Link
                  to="/landmarks/$slug"
                  params={{ slug: trigPillar.slug }}
                  style={{ textDecoration: "none" }}
                >
                  <Badge variant={trigPillar.visited ? "gold" : "neutral"} dot={trigPillar.visited}>
                    Trig pillar on this summit
                  </Badge>
                </Link>
              )}
            </div>
            <div style={{ marginTop: 14 }}>
              <StarRating itemType="peak" itemId={peak.id} initial={rating} isAuthenticated={isAuthenticated} />
            </div>
            {/* Page's single Summit Gold CTA (spec 34). A summit with a trig
                pillar points there instead: that check-in collects both. */}
            {isAuthenticated && !trigPillar && (
              <div style={{ marginTop: 16 }}>
                <Button variant="primary" onClick={() => setDialogOpen(true)}>
                  <Camera size={15} style={{ marginRight: 6 }} /> Check in at the summit
                </Button>
              </div>
            )}
            {isAuthenticated && trigPillar && (
              <div style={{ marginTop: 16 }}>
                <Link to="/landmarks/$slug" params={{ slug: trigPillar.slug }} style={{ textDecoration: "none" }}>
                  <Button variant="primary">
                    <Camera size={15} style={{ marginRight: 6 }} /> Check in at the trig pillar
                  </Button>
                </Link>
                <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text-muted)" }}>
                  A photo at the pillar collects the pillar and this summit.
                </div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <StatBlock label="Elevation" value={`${peak.elevationM} m`} icon={<Mountain size={12} />} />
            <StatBlock
              label="Prominence"
              value={peak.prominenceM != null ? `${peak.prominenceM} m` : "—"}
              icon={<TrendingUp size={12} />}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {isAuthenticated && <AddToListButton itemType="peak" itemId={peak.id} />}
            {peak.wikiUrl && (
              <a href={peak.wikiUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                <Button variant="secondary" leftIcon={<ExternalLink size={15} />} block>
                  Wikipedia
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>

      {lastResult && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--status-success)" }}>
          <Mountain size={15} />
          {lastResult.peakCredited?.firstVisit === false
            ? `Checked in. Another visit to ${peak.name} on the record.`
            : `Checked in. ${peak.name} is on the record.`}
        </div>
      )}

      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20 }}>
        {/* your visits */}
        <AtlasPanel title="Your visits">
          {!isAuthenticated ? (
            <EmptyState
              icon={<Calendar size={18} />}
              title="Log in to track your visits"
              hint="Connect an activity source and Atlas will log this peak automatically when your GPS track passes the summit, or check in with a photo taken at the top."
              action={{ label: "Log in", onClick: () => void navigate({ to: "/login" }) }}
            />
          ) : visits.length === 0 ? (
            <EmptyState
              icon={<Mountain size={18} />}
              title="Not summited yet"
              hint="Import an activity that passes this summit, or check in with a photo taken at the top. Either logs your summit here."
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {visits.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "10px 4px",
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>
                      {new Date(v.summitedAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    {v.notes && (
                      <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>{v.notes}</div>
                    )}
                  </div>
                  {v.activityId && (
                    <Link
                      to="/activities/$id/review"
                      params={{ id: v.activityId }}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "var(--gold-400)",
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {v.activityName ?? "View activity"}
                    </Link>
                  )}
                  {!v.activityId && v.sourceType === "checkin" && (
                    <span style={{ fontSize: 12.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      Photo check-in
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </AtlasPanel>

        {/* in collections */}
        <AtlasPanel title="In collections">
          {collections.length === 0 ? (
            <EmptyState icon={<Users size={18} />} title="Not part of any curated collection" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {collections.map((col) => (
                <CollectionCard
                  key={col.slug}
                  title={col.name}
                  type="Collection"
                  value={col.completedCount}
                  max={col.itemCount}
                  icon={<Mountain size={22} />}
                  onClick={() => void navigate({ to: "/collections/$slug", params: { slug: col.slug } })}
                />
              ))}
            </div>
          )}
        </AtlasPanel>
      </div>

      {/* check-in gallery: summit check-ins plus trig pillar check-ins that collected this peak */}
      <CheckinGallery target={checkinTarget} isAuthenticated={isAuthenticated} />

      {/* nearby uncollected */}
      <AtlasPanel title="Nearby uncollected">
        {nearbyUncollected.length === 0 ? (
          <EmptyState icon={<MapPinned size={18} />} title="Nothing uncollected nearby" />
        ) : (
          <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
            {nearbyUncollected.map((p) => (
              <CollectibleItem
                key={p.id}
                name={p.name}
                meta={
                  <>
                    <b>{p.elevationM}m</b> · {p.distanceKm}km
                  </>
                }
                icon={<Mountain size={26} />}
                onClick={() => void navigate({ to: "/peaks/$slug", params: { slug: p.slug } })}
              />
            ))}
          </div>
        )}
      </AtlasPanel>

      {dialogOpen && (
        <CheckinDialog target={checkinTarget} onClose={() => setDialogOpen(false)} onSuccess={handleCheckinSuccess} />
      )}
    </div>
  );
}

export default function PeakDetailPage() {
  const params = useParams({ strict: false });
  const slug = (params as { slug?: string }).slug ?? "";
  const { user: me, isLoading: meLoading } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["peaks", slug],
    queryFn: () => fetchPeak(slug),
    enabled: !!slug,
    retry: false,
  });

  let content: React.ReactNode;

  if (isLoading || meLoading) {
    content = (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="200px" />
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20 }}>
          <Skel height="220px" />
          <Skel height="220px" />
        </div>
        <Skel height="160px" />
      </div>
    );
  } else if (isError || !data) {
    content = (
      <div className="r-page" style={{ padding: 28, flex: 1, overflow: "auto" }}>
        <EmptyState icon={<Mountain size={18} />} title="Peak not found" />
      </div>
    );
  } else {
    content = <PeakContent data={data} isAuthenticated={!!me} />;
  }

  if (me) {
    return <AppShell>{content}</AppShell>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", flexDirection: "column" }}>
      <GuestTopBar />
      <div className="atlas-guest-column">{content}</div>
    </div>
  );
}
